import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

import tajawalFont from "../assets/font/Tajawal-Regular.ttf";

Font.register({
  family: "Tajawal",
  fonts: [
    {
      src: tajawalFont,
    },
  ],
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
    fontSize: 10,
    fontFamily: "Tajawal",
  },
  arabicText: {
    fontFamily: "Tajawal",
    textAlign: "right",
    direction: "rtl",
  },
  englishText: {
    fontFamily: "Tajawal",
    textAlign: "left",
    direction: "ltr",
  },
  header: {
    textAlign: "center",
    fontSize: 24,
    fontWeight: "bold",
    color: "#79329a",
    marginBottom: 5,
    paddingBottom: 5,
  },
  line: {
    borderBottomWidth: 2,
    borderBottomColor: "#79329a",
    marginBottom: 30,
  },
  customerSection: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#f8f9fa",
    borderRadius: 5,
  },
  customerTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#1f2937",
  },
  customerDetails: {
    flexDirection: "column",
    gap: 5,
  },
  customerRow: {
    flexDirection: "row",
    marginBottom: 3,
    fontSize: 10,
  },
  customerRowRTL: {
    flexDirection: "row-reverse",
    marginBottom: 3,
    fontSize: 10,
  },
  customerLabel: {
    fontWeight: "bold",
    marginRight: 8,
    width: 80,
  },
  customerLabelRTL: {
    fontWeight: "bold",
    marginLeft: 8,
    width: 80,
    textAlign: "right",
  },
  dateSection: {
    textAlign: "center",
    fontSize: 12,
    marginBottom: 20,
    color: "#6b7280",
  },
  tableContainer: {
    marginBottom: 20,
  },
  table: {
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#d1d5db",
    marginBottom: 20,
  },
  tableWrapper: {
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#d1d5db",
    minPresenceAhead: 100,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#e5e7eb",
    borderBottomWidth: 2,
    borderBottomColor: "#9ca3af",
    fontWeight: "bold",
  },
  tableHeaderRTL: {
    flexDirection: "row-reverse",
    backgroundColor: "#e5e7eb",
    borderBottomWidth: 2,
    borderBottomColor: "#9ca3af",
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
  },
  tableRowRTL: {
    flexDirection: "row-reverse",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
  },
  tableRowContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    orphans: 3,
    widows: 3,
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#d1d5db",
  },
  tableCellLast: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    textAlign: "center",
  },
  summarySection: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginTop: 20,
    gap: 10,
  },
  summaryBox: {
    flex: 1,
    padding: 15,
    borderRadius: 5,
    textAlign: "center",
  },
  remainingBox: {
    backgroundColor: "#ddecf6",
    borderWidth: 1,
    borderColor: "#426f8c",
  },
  receivedBox: {
    backgroundColor: "#dcfce7",
    borderWidth: 1,
    borderColor: "#16a34a",
  },
  totalBox: {
    backgroundColor: "#f3f4f6",
    borderWidth: 1,
    borderColor: "#6b7280",
  },
  boxTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  boxAmount: {
    fontSize: 16,
    fontWeight: "bold",
  },
  remainingAmount: {
    color: "#dc2626",
  },
  receivedAmount: {
    color: "#16a34a",
  },
  totalAmount: {
    color: "#6b7280",
  },
  flexRow: {
    display: "flex",
    justifyContent: "space-between",
  },
});

const TransactionStatementPDF = ({
  language = "EN",
  companyName = "Your Company Name",
  data = {},
  transactions = [],
  isCustomer = true,
  bankName = "",
}) => {
  const isArabic = language === "AR";

  // Configuration for pagination
  const RECORDS_PER_PAGE = 15; // Adjust this number based on your needs

  // Helper function to split transactions into pages
  const paginateTransactions = (transactions) => {
    const pages = [];
    for (let i = 0; i < transactions.length; i += RECORDS_PER_PAGE) {
      pages.push(transactions.slice(i, i + RECORDS_PER_PAGE));
    }
    return pages.length > 0 ? pages : [[]]; // Ensure at least one page
  };

  // Translations
  const translations = {
    EN: {
      customerDetails: "Customer Details",
      customerName: "Name",
      customerAddress: "Address",
      phoneNumber: "Phone",
      email: "Email",
      currentDate: "Statement Date",
      transaction: "Transaction",
      date: "Date",
      amount: "Amount",
      remainingAmount: "Remaining Amount",
      receivedAmount: "Received Amount",
      totalAmount: "Total Amount",
      bankName: "Bank Name",
      transactionReport: "Transaction Report",
      balance: "Balance",
    },
    AR: {
      bankName: "إسم المصرف",
      customerDetails: "بيانات العميل",
      customerName: "الاسم",
      customerAddress: "العنوان",
      phoneNumber: "رقم الهاتف",
      email: "البريد الإلكتروني",
      currentDate: "تاريخ الكشف",
      transaction: "الحركة",
      date: "التاريخ",
      amount: "المبلغ",
      remainingAmount: "المبلغ المتبقي",
      receivedAmount: "المبلغ المستلم",
      totalAmount: "إجمالي المبلغ",
      alhabbarSlogan: "شركة الحبّار للتطوير البرمجي",
      ehsiblySecondSlogan: "برنامج إحسبلي للإدارة والمحاسبة",
      transactionReport: "كشف حساب",
      balance: "الرصيد",
    },
  };

  const t = translations[language] || translations.EN;

  // Extract data from props
  const customer = data.customer || {};
  const transactionsData = data.transactions || {};
  const transactionRecords = transactionsData || [];

  // Customer data
  const customerData = {
    name: customer.name || (isArabic ? "اسم العميل" : "Customer Name"),
    address:
      customer.address || (isArabic ? "عنوان العميل" : "Customer Address"),
    phone: customer.phoneNumber || "+964 000 000 0000",
    email: customer.email || "customer@email.com",
  };

  // Summary data
  const summary = {
    total: transactionsData.total || 0,
    received: transactionsData.received || 0,
    remaining: transactionsData.balance || 0,
    balance: data.balance,
  };

  // Get customer currency or default to USD
  const currency = customer.currency || "USD";

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US").format(amount) + ` ${currency}`;
  };

  // Format date from ISO string
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-UK");
  };

  // Get current date
  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-UK");
  };

  // Helper component for styled text
  const StyledText = ({ children, style = {} }) => {
    const baseStyle = isArabic ? styles.arabicText : styles.englishText;
    return <Text style={{ ...baseStyle, ...style }}>{children}</Text>;
  };

  // Get paginated transactions
  const paginatedTransactions = paginateTransactions(transactions);

  return (
    <Document>
      {paginatedTransactions.map((pageTransactions, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          {/* Header - Company Name - Show on all pages */}
          <View style={styles.line}>
            <StyledText style={styles.header}>{companyName}</StyledText>
            <StyledText style={styles.dateSection}>
              {t.transactionReport}
            </StyledText>
          </View>

          {/* Customer Details Section - Only show on first page */}
          {isCustomer && pageIndex === 0 && (
            <View style={styles.customerSection}>
              <StyledText style={styles.customerTitle}>
                {t.customerDetails}
              </StyledText>
              <View style={styles.customerDetails}>
                <View
                  style={isArabic ? styles.customerRowRTL : styles.customerRow}
                >
                  <StyledText
                    style={
                      isArabic ? styles.customerLabelRTL : styles.customerLabel
                    }
                  >
                    {t.customerName}:
                  </StyledText>
                  <StyledText>{customerData.name}</StyledText>
                </View>
                <View
                  style={isArabic ? styles.customerRowRTL : styles.customerRow}
                >
                  <StyledText
                    style={
                      isArabic ? styles.customerLabelRTL : styles.customerLabel
                    }
                  >
                    {t.customerAddress}:
                  </StyledText>
                  <StyledText>{customerData.address}</StyledText>
                </View>
                <View
                  style={isArabic ? styles.customerRowRTL : styles.customerRow}
                >
                  <StyledText
                    style={
                      isArabic ? styles.customerLabelRTL : styles.customerLabel
                    }
                  >
                    {t.phoneNumber}:
                  </StyledText>
                  <StyledText>{customerData.phone}</StyledText>
                </View>
                <View
                  style={isArabic ? styles.customerRowRTL : styles.customerRow}
                >
                  <StyledText
                    style={
                      isArabic ? styles.customerLabelRTL : styles.customerLabel
                    }
                  >
                    {t.email}:
                  </StyledText>
                  <StyledText>{customerData.email}</StyledText>
                </View>
              </View>
            </View>
          )}

          {/* Current Date - Show on all pages */}
          <View style={styles.flexRow}>
            {!isCustomer && (
              <StyledText style={styles.dateSection}>
                {t.bankName}: {bankName}
              </StyledText>
            )}
            <StyledText style={styles.dateSection}>
              {t.currentDate}: {getCurrentDate()}
            </StyledText>
          </View>

          {/* Transactions Table */}
          <View style={styles.tableContainer}>
            <View style={styles.tableWrapper}>
              {/* Table Header - Show on every page */}
              <View
                style={isArabic ? styles.tableHeaderRTL : styles.tableHeader}
              >
                <StyledText style={styles.tableCell}>
                  {t.transaction}
                </StyledText>
                <StyledText style={styles.tableCell}>{t.date}</StyledText>
                <StyledText style={styles.tableCellLast}>{t.amount}</StyledText>
              </View>

              {/* Table Rows for this page */}
              {pageTransactions.map((transaction, index) => (
                <View
                  key={index}
                  style={[
                    isArabic ? styles.tableRowRTL : styles.tableRow,
                    styles.tableRowContainer,
                  ]}
                >
                  {isArabic ? (
                    <>
                      <StyledText style={styles.tableCell}>
                        {formatCurrency(transaction.amount)}
                      </StyledText>
                      <StyledText style={styles.tableCell}>
                        {formatDate(transaction.date)}
                      </StyledText>
                      <StyledText style={styles.tableCellLast}>
                        {transaction.name === null
                          ? transaction.type
                          : transaction.name}
                      </StyledText>
                    </>
                  ) : (
                    <>
                      <StyledText style={styles.tableCell}>
                        {transaction.name === null
                          ? transaction.type
                          : transaction.name}
                      </StyledText>
                      <StyledText style={styles.tableCell}>
                        {formatDate(transaction.date)}
                      </StyledText>
                      <StyledText style={styles.tableCellLast}>
                        {formatCurrency(transaction.amount)}
                      </StyledText>
                    </>
                  )}
                </View>
              ))}
            </View>
          </View>

          {/* Summary Boxes - Only show on last page */}
          {pageIndex === paginatedTransactions.length - 1 && (
            <>
              {isCustomer ? (
                <View style={styles.summarySection}>
                  {/* Remaining Amount Box */}
                  <View style={[styles.summaryBox, styles.remainingBox]}>
                    <StyledText style={styles.boxTitle}>{t.balance}</StyledText>
                    <StyledText
                      style={[styles.boxAmount, styles.remainingAmount]}
                    >
                      {formatCurrency(summary.remaining)}
                    </StyledText>
                  </View>

                  {/* Received Amount Box */}
                  <View style={[styles.summaryBox, styles.receivedBox]}>
                    <StyledText style={styles.boxTitle}>
                      {t.receivedAmount}
                    </StyledText>
                    <StyledText
                      style={[styles.boxAmount, styles.receivedAmount]}
                    >
                      {formatCurrency(summary.received)}
                    </StyledText>
                  </View>

                  {/* Total Amount Box - Commented out in original */}
                  {/* <View style={[styles.summaryBox, styles.totalBox]}>
                    <StyledText style={styles.boxTitle}>{t.totalAmount}</StyledText>
                    <StyledText style={[styles.boxAmount, styles.totalAmount]}>
                      {formatCurrency(summary.total)}
                    </StyledText>
                  </View> */}
                </View>
              ) : (
                <View style={styles.summarySection}>
                  <View style={[styles.summaryBox, styles.totalBox]}>
                    <StyledText style={styles.boxTitle}>
                      {t.totalAmount}
                    </StyledText>
                    <StyledText style={[styles.boxAmount, styles.totalAmount]}>
                      {formatCurrency(summary.balance)}
                    </StyledText>
                  </View>
                </View>
              )}
            </>
          )}

          {/* Footer Section - Show on all pages */}
          <View
            style={{
              position: "absolute",
              bottom: 20,
              left: 20,
              right: 20,
              textAlign: "center",
              paddingTop: 10,
              borderTopWidth: 1,
              borderTopColor: "#d1d5db",
              flexDirection: isArabic ? "column-reverse" : "column",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                fontSize: 8,
                fontFamily: "Tajawal",
              }}
            >
              {t.alhabbarSlogan}
            </Text>
            <Text
              style={{
                fontSize: 10,
                fontWeight: "bold",
                marginBottom: 4,
                fontFamily: "Tajawal",
              }}
            >
              {t.ehsiblySecondSlogan}
            </Text>

            {/* Page number if multiple pages */}
            {paginatedTransactions.length > 1 && (
              <Text
                style={{
                  fontSize: 8,
                  fontFamily: "Tajawal",
                  marginTop: 5,
                  color: "#6b7280",
                }}
              >
                Page {pageIndex + 1} of {paginatedTransactions.length}
              </Text>
            )}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default TransactionStatementPDF;
